import type { ProjectDataSubscriptionUpdate } from '@/backend/contracts';
import type { MetaSlice } from '../../store/types';

type CloudConnectionState = MetaSlice['cloud'];

export type RemoteProjectUpdateWithControls = ProjectDataSubscriptionUpdate;

type SubscriptionControlContext = {
    stateGetter: () => { cloud: CloudConnectionState };
    safeSetCloudState: (updates: Partial<CloudConnectionState>) => void;
};

const CONTROL_FIELD_KEYS: ReadonlySet<string> = new Set([
    '_scriptTextAccess',
    '_isBeta',
    '_communicationAudiences',
    '_communicationSettings',
    '_member',
]);

export function hasSubscriptionResourcePayload(update: RemoteProjectUpdateWithControls): boolean {
    return Object.keys(update).some(key => !CONTROL_FIELD_KEYS.has(key));
}

export function applySubscriptionControlFields(
    stateUpdate: RemoteProjectUpdateWithControls,
    context: SubscriptionControlContext,
): void {
    if (typeof stateUpdate._scriptTextAccess === 'boolean') {
        const currentCloud = context.stateGetter().cloud;
        if (currentCloud.scriptTextAccess !== stateUpdate._scriptTextAccess) {
            context.safeSetCloudState({
                ...currentCloud,
                scriptTextAccess: stateUpdate._scriptTextAccess,
            });
        }
        delete stateUpdate._scriptTextAccess;
    }

    if (typeof stateUpdate._isBeta === 'boolean') {
        const currentCloud = context.stateGetter().cloud;
        if (currentCloud.isBeta !== stateUpdate._isBeta) {
            context.safeSetCloudState({
                ...currentCloud,
                isBeta: stateUpdate._isBeta,
            });
        }
        delete stateUpdate._isBeta;
    }

    if (stateUpdate._communicationAudiences) {
        context.safeSetCloudState({
            ...context.stateGetter().cloud,
            communicationAudiences: stateUpdate._communicationAudiences,
        });
        delete stateUpdate._communicationAudiences;
    }

    if (stateUpdate._communicationSettings) {
        context.safeSetCloudState({
            ...context.stateGetter().cloud,
            communicationSettings: stateUpdate._communicationSettings,
        });
        delete stateUpdate._communicationSettings;
    }

    if ('_member' in stateUpdate) {
        context.safeSetCloudState({
            ...context.stateGetter().cloud,
            member: stateUpdate._member ?? null,
        });
        delete stateUpdate._member;
    }
}
